import { useEffect, useState } from 'react';

import {
  DesignerUIStateActions,
  useDesignerUIContext,
} from './useDesignerUIState';

type StepState = 'pending' | 'loading' | 'completed';

export function useLoaderState(gameId?: string | null) {
  const dispatch = useDesignerUIContext(context => context.dispatch);
  const loaderState = useDesignerUIContext(
    context => (gameId ? context.state.loaderStates[gameId] : undefined),
  );
  
  // Use local state to trigger re-renders every 500ms
  const [, setTick] = useState(0);

  // #region agent log
  fetch('http://127.0.0.1:7242/ingest/66e7584c-4125-45e5-89a6-3a48c5d80c2c',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'useLoaderState.ts:18',message:'Hook called',data:{gameId,hasLoaderState:!!loaderState},timestamp:Date.now(),sessionId:'debug-session',runId:'simple',hypothesisId:'ALL'})}).catch(()=>{});
  // #endregion

  // Initialize state if gameId exists and state doesn't
  useEffect(() => {
    if (!gameId || loaderState) return;
    
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/66e7584c-4125-45e5-89a6-3a48c5d80c2c',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'useLoaderState.ts:28',message:'Initializing state',data:{gameId},timestamp:Date.now(),sessionId:'debug-session',runId:'simple',hypothesisId:'INIT'})}).catch(()=>{});
    // #endregion
    dispatch({
      type: DesignerUIStateActions.initLoaderState,
      payload: { gameId },
    });
  }, [gameId, loaderState, dispatch]);

  // Trigger re-renders every 500ms to update the calculated state
  useEffect(() => {
    if (!gameId || !loaderState) return;
    
    const interval = setInterval(() => {
      setTick(t => t + 1);
    }, 500);

    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/66e7584c-4125-45e5-89a6-3a48c5d80c2c',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'useLoaderState.ts:48',message:'Interval started',data:{gameId},timestamp:Date.now(),sessionId:'debug-session',runId:'simple',hypothesisId:'INTERVAL'})}).catch(()=>{});
    // #endregion

    return () => {
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/66e7584c-4125-45e5-89a6-3a48c5d80c2c',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'useLoaderState.ts:54',message:'Interval cleanup',data:{gameId},timestamp:Date.now(),sessionId:'debug-session',runId:'simple',hypothesisId:'CLEANUP'})}).catch(()=>{});
      // #endregion
      clearInterval(interval);
    };
  }, [gameId, loaderState]);

  // Return default state if not initialized yet
  if (!loaderState) {
    return {
      stepStates: ['pending', 'pending', 'pending', 'pending'] as StepState[],
      showPin: false,
    };
  }

  // Calculate current state based on elapsed time (pure calculation!)
  const elapsed = Date.now() - loaderState.startTime;
  
  // #region agent log
  fetch('http://127.0.0.1:7242/ingest/66e7584c-4125-45e5-89a6-3a48c5d80c2c',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'useLoaderState.ts:72',message:'Calculating state',data:{gameId,elapsed},timestamp:Date.now(),sessionId:'debug-session',runId:'simple',hypothesisId:'CALC'})}).catch(()=>{});
  // #endregion

  let stepStates: StepState[];
  let showPin = false;

  if (elapsed < 4000) {
    // Step 1: First 4 seconds
    stepStates = ['loading', 'pending', 'pending', 'pending'];
    showPin = false;
  } else if (elapsed < 8000) {
    // Step 2: 4-8 seconds
    stepStates = ['completed', 'loading', 'pending', 'pending'];
    showPin = true;
  } else if (elapsed < 12000) {
    // Step 3: 8-12 seconds
    stepStates = ['completed', 'completed', 'loading', 'pending'];
    showPin = true;
  } else {
    // Step 4: After 12 seconds
    stepStates = ['completed', 'completed', 'completed', 'loading'];
    showPin = true;
  }

  return { stepStates, showPin };
}
